<template>
  <div class="row q-ma-sm">
    <Wrapper class="q-pa-lg" style="min-height: 400px">
      <div class="flex justify-between q-mb-lg">
        <q-btn icon="chevron_left" color="primary" label="Back to List" @click="goBack" />
      </div>

      <div class="container-fluid">
        <div v-if="title" class="row">
          <h4 class="q-mb-md">{{ title }}</h4>
        </div>

        <div v-if="isLoading" class="flex justify-center">
          <q-spinner color="primary" size="xl" :thickness="3" />
        </div>

        <template v-else>
          <div class="row q-col-gutter-sm">
            <!-- Response or request ID -->
            <div class="col-6">
              <template v-if="response">
                <span class="text-weight-medium">
                  <span v-if="isAcceptedQuote">Booking ID: </span>
                  <span v-else>Quote ID: </span>
                  <span class="text-weight-regular">
                    {{ response.id | formatId }}
                    <q-tooltip anchor="center right" self="center left">
                      RFQ ID: {{ request.id | formatId }}
                    </q-tooltip>
                  </span>
                </span>
              </template>

              <template v-else>
                <span class="text-weight-medium"
                  >RFQ ID:
                  <span class="text-weight-regular">{{ request.id | formatId }}</span>
                </span>
              </template>
            </div>

            <div class="col-6">
              <template v-if="showCustomer">
                <span class="text-weight-medium">Customer: </span>
                <span class="text-capitalize"
                  >{{ request.shipper.name }}
                  <span class="text-uppercase">{{ request.shipper.businessName }}</span>
                </span>
              </template>

              <template v-else-if="response">
                <template v-if="response.transporter">
                  <span class="text-weight-medium">Transporter: </span>
                  <span class="text-uppercase">{{ response.company.tradingName }}</span>
                </template>

                <template v-else-if="response.shipper">
                  <span class="text-weight-medium">Customer: </span>
                  <span class="text-capitalize"
                    >{{ request.shipper.name }}
                    <span class="text-uppercase">{{ request.shipper.businessName }}</span>
                  </span>
                </template>
              </template>
            </div>

            <div class="col-12 col-sm-6 q-mb-sm">
              <q-btn-group outline :spread="$q.screen.lt.xl">
                <q-btn outline icon="save_alt" color="primary" label="PDF" @click="savePdf" />
                <q-btn
                  v-if="isAcceptedQuote"
                  outline
                  icon="history"
                  color="primary"
                  label="History"
                  @click="historyDialog = true"
                />
                <q-btn-dropdown outline color="primary" icon="more_horiz" label="More">
                  <q-list>
                    <q-item v-close-popup clickable :disable="!canBeProhibited" @click="isProhibitDialogOpen = true">
                      <q-item-section avatar>
                        <q-icon name="block" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>Prohibit</q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-item v-close-popup clickable>
                      <q-item-section avatar>
                        <q-icon name="edit" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>Modify</q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-item v-if="isAcceptedQuote" v-close-popup clickable @click="historyDialog = true">
                      <q-item-section avatar>
                        <q-icon name="history" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>History</q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-btn-dropdown>
              </q-btn-group>
            </div>

            <div v-if="request.responses" class="col-12 col-sm-6 flex items-center">
              <span class="inline-block text-weight-medium"
                >Status:
                <span v-if="request.deletedAt" class="text-weight-bold text-negative"> Prohibited </span>
                <span
                  v-else
                  class="text-weight-bold"
                  :class="getStatusClass(getStatus(response, !!request.responses.length))"
                >
                  {{ getStatus(response, !!request.responses.length) || 'Quote is ready' | uppercase }}
                </span>
              </span>
              <span
                v-if="['Pending', 'Confirmed', 'Completed'].includes(getStatus(response, !!request.responses.length))"
              >
                <q-icon class="q-ml-xs" name="info_outline" color="primary" size="sm" />
                <q-tooltip
                  class="md-tooltip-custom"
                  anchor="center right"
                  self="center left"
                  content-style="fontSize: 0.875rem"
                >
                  {{ getTooltipText(response) }}
                </q-tooltip>
              </span>
            </div>
          </div>

          <PDFPreview
            v-if="request.id"
            :request-from-client="request"
            :response-from-client="response ? response : {}"
          />
        </template>
        <report v-if="$route.name === 'shipperBookingDetail' && response" :user="response.transporter" />
      </div>
    </Wrapper>

    <q-dialog v-model="isPdfLoading" persistent>
      <q-card style="width: 300px">
        <q-card-section class="flex column justify-center items-center">
          <q-spinner color="primary" size="lg" :thickness="3" />

          <p class="q-mt-md">Please wait...</p>
        </q-card-section>
      </q-card>
    </q-dialog>

    <prohibit-dialog
      v-if="isProhibitDialogOpen"
      :is-open.sync="isProhibitDialogOpen"
      :request-id="id"
      @prohibited="getRequest"
    />

    <q-dialog v-model="historyDialog" @before-show="getHistory">
      <q-card style="width: 700px; max-width: 80vw">
        <q-card-section>
          <h4 class="text-center">History</h4>
        </q-card-section>
        <q-card-section style="max-height: 70vh" class="scroll">
          <q-timeline layout="comfortable" color="secondary">
            <q-timeline-entry
              v-for="log in logs"
              :key="log.id"
              :title="log.action"
              :subtitle="log.createdAt | formatDateTime"
            />
          </q-timeline>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat color="muted" label="Cancel" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { getStatusClass, getStatus, getErrorMessage } from '@/utils';
import Wrapper from '@/components/Wrapper.vue';
import PDFPreview from '@/components/PDFPreview.vue';
import Report from '@/components/Report.vue';
import formatDateMixin from '@/utils/mixins/formatDateMixin';
import API from '@/apis';

export default {
  name: 'RFQDetail',

  components: {
    Report,
    Wrapper,
    PDFPreview,
    ProhibitDialog: () => import('./ProhibitDialog.vue'),
  },

  mixins: [formatDateMixin],

  props: {
    id: {
      type: String,
      required: true,
    },
    showCustomer: {
      type: Boolean,
      default: false,
    },
    responseId: {
      required: false,
      type: String,
      default: null,
    },
    title: {
      required: false,
      type: String,
      default: null,
    },
  },

  data() {
    return {
      request: {},
      response: null,
      isLoading: false,
      isPdfLoading: false,
      isAcceptedQuote: false,
      isProhibitDialogOpen: false,
      pdf: null,
      historyDialog: false,
      logs: [],
    };
  },

  computed: {
    ...mapState(['user']),
    ...mapState('chat', { stateUnread: 'unreadMessages' }),

    unreadMessages() {
      const unreadArr = this.stateUnread.filter(
        (message) => message.bookingConversationId === this.response.bookingConversationId
      );

      return unreadArr.length;
    },
    canBeProhibited() {
      return (
        (this.response?.status === null && this.request.deletedAt === null) ||
        (!this.response && this.request.deletedAt === null)
      );
    },
  },

  created() {
    this.getRequest();
  },

  methods: {
    async getRequest() {
      this.isLoading = true;

      try {
        const { data } = await this.$axios.get(`/quoteRequest/${this.id}`);
        const { acceptedResponse, ...request } = data;
        this.request = request;

        // If responseId exists, try to find response with matching ID
        if (this.responseId) {
          if (this.responseId === acceptedResponse?.id) {
            this.response = acceptedResponse;
          }

          this.response = request.responses.find((res) => res.id === this.responseId);
          // Use accepted response (if exists) otherwise
        } else if (acceptedResponse) {
          this.response = acceptedResponse;

          this.$socket.client.emit('docs', {
            conversationId: acceptedResponse.bookingConversationId,
          });
        }

        // Wait for response to be processed then check if response is the accepted one
        this.$nextTick(() => {
          if (acceptedResponse && this.response?.id === acceptedResponse?.id) {
            this.isAcceptedQuote = true;
          }
        });
      } catch (error) {
        this.$q.notify({ type: 'negative', message: getErrorMessage(error) });
      } finally {
        this.isLoading = false;
      }
    },
    async getHistory() {
      if (!this.response) {
        return;
      }

      try {
        const { data } = await this.$axios.request(API.quotes.getResponseLogs(this.response.id));
        this.logs = data;
      } catch (error) {
        this.$q.notify({
          type: 'negative',
          message: getErrorMessage(error),
        });
      }
    },
    async savePdf() {
      if (!this.pdf) {
        try {
          this.isPdfLoading = true;
          const config = {
            responseType: 'arraybuffer',
            headers: { Accept: 'application/pdf' },
          };

          config.params = this.response ? { response: this.response.id } : { forceRequest: true };
          config.params.tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Sydney';

          const { data } = await this.$axios.get(`/quoteRequest/${this.request.id}/pdf`, config);
          this.pdf = new Blob([data], { type: 'application/pdf' });
        } catch (error) {
          this.$q.notify({ message: getErrorMessage(error), type: 'negative' });

          return;
        } finally {
          this.isPdfLoading = false;
        }
      }
      const link = document.createElement('a');
      link.href = URL.createObjectURL(this.pdf);

      if (this.response) {
        const prefix = this.isAcceptedQuote ? 'Booking' : 'Quote';
        link.download = `${prefix}-${this.response.id}.pdf`;
      } else {
        link.download = `RFQ-${this.request.id}.pdf`;
      }

      link.click();
      link.remove();
    },
    goBack() {
      this.$emit('back');
    },
    getStatus,
    getStatusClass,
    getTooltipText(response) {
      switch (response.status) {
        case 'Pending':
          return 'Your booking is waiting to be confirmed by the transporter.';
        case 'Confirmed':
          return 'The transporter has accepted your booking.';
        case 'Completed':
          return 'The booking has been completed.';
        default:
          return '';
      }
    },
  },
};
</script>
